import { RichTextExternalVideoBlockNode } from '@cinemark/content-api-client';
import type { SerializedBlockNode } from '@payloadcms/richtext-lexical';
import clsx from 'clsx';
import React from 'react';

type RichTextExternalVideoBlockComponentProps = {
  node: SerializedBlockNode | undefined;
};

export const RichTextExternalVideoBlockComponent = ({ node }: RichTextExternalVideoBlockComponentProps): React.JSX.Element | null => {
  if (!node || node.type !== 'block' || !node.fields || typeof node.fields !== 'object' || node.fields.blockType !== 'externalVideo') return null;

  const fields = node.fields as unknown as RichTextExternalVideoBlockNode;

  const getAspectRatioClass = (aspectRatio?: string): string => {
    switch (aspectRatio) {
      case '16/9': return 'aspect-video';
      case '4/3': return 'aspect-4/3';
      case '3/2': return 'aspect-3/2';
      case '1/1': return 'aspect-square';
      case '9/16': return 'aspect-9/16';
      case '2/3': return 'aspect-2/3';
      default: return 'aspect-video';
    }
  };

  const getAlignmentClass = (alignment?: string): string => {
    switch (alignment) {
      case 'left': return 'mx-0';
      case 'center': return 'mx-auto';
      case 'right': return 'ml-auto mr-0';
      default: return 'mx-0';
    }
  };

  const buildEmbedUrl = (videoId: string, embedBaseUrl: string, muteParam: string): string => {
    const params = new URLSearchParams();
    if (fields.autoplay) params.append('autoplay', '1');
    if (fields.muted) params.append(muteParam, '1');
    if (!fields.showControls) params.append('controls', '0');

    const queryString = params.toString();
    return `${embedBaseUrl}${videoId}${queryString ? `?${queryString}` : ''}`;
  };

  const mergeUrlParams = (
    existingParams: URLSearchParams,
    paramMappings: Record<string, {
      falseValue: string;
      fieldValue: boolean | undefined;
      paramName: string;
      trueValue: string;
    }>
  ): URLSearchParams => {
    const params = new URLSearchParams();

    for (const [, config] of Object.entries(paramMappings)) {
      if (config.fieldValue) {
        params.append(config.paramName, config.trueValue);
      } else if (existingParams.has(config.paramName) && existingParams.get(config.paramName) !== config.falseValue) {
        params.append(config.paramName, existingParams.get(config.paramName) || config.falseValue);
      }
    }

    const managedParams = Object.values(paramMappings).map(config => config.paramName);
    for (const [key, value] of existingParams.entries()) {
      if (!managedParams.includes(key)) {
        params.append(key, value);
      }
    }

    return params;
  };

  const handleYouTubeEmbedUrl = (urlObj: URL): string | null => {
    // Remove '/embed/'
    const embedPath = urlObj.pathname.slice(7);

    if (!embedPath) return null;

    const existingParams = new URLSearchParams(urlObj.search);
    const paramMappings = {
      autoplay: {
        falseValue: '0',
        fieldValue: fields.autoplay,
        paramName: 'autoplay',
        trueValue: '1',
      },
      controls: {
        falseValue: '1',
        fieldValue: !fields.showControls,
        paramName: 'controls',
        trueValue: '0',
      },
      muted: {
        falseValue: '0',
        fieldValue: fields.muted,
        paramName: 'mute',
        trueValue: '1',
      },
    };

    const params = mergeUrlParams(existingParams, paramMappings);
    const queryString = params.toString();
    return `https://www.youtube.com/embed/${embedPath}${queryString ? `?${queryString}` : ''}`;
  };

  const handleVimeoEmbedUrl = (urlObj: URL): string | null => {
    // Remove '/video/'
    const videoId = urlObj.pathname.slice(7);
    if (!videoId || !/^\d+$/.test(videoId)) return null;

    const existingParams = new URLSearchParams(urlObj.search);
    const paramMappings = {
      autoplay: {
        falseValue: '0',
        fieldValue: fields.autoplay,
        paramName: 'autoplay',
        trueValue: '1',
      },
      muted: {
        falseValue: '0',
        fieldValue: fields.muted,
        paramName: 'muted',
        trueValue: '1',
      },
    };

    const params = mergeUrlParams(existingParams, paramMappings);
    const queryString = params.toString();
    return `https://player.vimeo.com/video/${videoId}${queryString ? `?${queryString}` : ''}`;
  };

  const handleFacebookEmbedUrl = (urlObj: URL): string | null => {
    let originalUrl: string;
    if (urlObj.pathname === '/plugins/video.php') {
      const href = urlObj.searchParams.get('href');
      if (!href) return null;
      originalUrl = href;
    } else {
      originalUrl = urlObj.href;
    }

    const params = new URLSearchParams();
    params.append('href', originalUrl);
    if (fields.autoplay) params.append('autoplay', '1');
    if (fields.muted) params.append('muted', '1');
    if (!fields.showControls) params.append('show_text', '0');

    const queryString = params.toString();
    return `https://www.facebook.com/plugins/video.php?${queryString}`;
  };

  const getEmbedUrl = (url: string): string | null => {
    if (!fields.videoUrl || typeof fields.videoUrl !== 'string' || fields.videoUrl.trim() === '') {
      return null;
    }

    let urlObj: URL;
    try {
      urlObj = new URL(url);
    } catch {
      return null;
    }

    const allowedYouTubeHosts = new Set(['youtube.com', 'www.youtube.com']);

    if (allowedYouTubeHosts.has(urlObj.hostname) && urlObj.pathname.startsWith('/watch')) {
      const videoId = urlObj.searchParams.get('v');
      if (videoId) {
        return buildEmbedUrl(videoId, 'https://www.youtube.com/embed/', 'mute');
      }
    }

    if (allowedYouTubeHosts.has(urlObj.hostname) && urlObj.pathname.startsWith('/embed/')) {
      return handleYouTubeEmbedUrl(urlObj);
    }

    if (urlObj.hostname === 'youtu.be') {
      const videoId = urlObj.pathname.slice(1).split('/')[0];
      if (videoId) {
        return buildEmbedUrl(videoId, 'https://www.youtube.com/embed/', 'mute');
      }
    }

    const allowedVimeoHosts = new Set(['vimeo.com', 'www.vimeo.com']);

    if (allowedVimeoHosts.has(urlObj.hostname) && !urlObj.pathname.startsWith('/video/')) {
      const pathParts = urlObj.pathname.split('/');
      const videoId = pathParts.filter(Boolean)[0];
      if (videoId && /^\d+$/.test(videoId)) {
        return buildEmbedUrl(videoId, 'https://player.vimeo.com/video/', 'muted');
      }
    }

    if (urlObj.hostname === 'player.vimeo.com' && urlObj.pathname.startsWith('/video/')) {
      return handleVimeoEmbedUrl(urlObj);
    }

    const allowedFacebookHosts = new Set(['facebook.com', 'www.facebook.com', 'fb.watch', 'm.facebook.com']);

    if (allowedFacebookHosts.has(urlObj.hostname)) {
      if (urlObj.pathname.includes('/videos/') || urlObj.pathname.includes('/watch/') || urlObj.hostname === 'fb.watch'
      || urlObj.pathname === '/plugins/video.php') {
        return handleFacebookEmbedUrl(urlObj);
      }
    }

    return null;
  };

  const embedUrl = getEmbedUrl(fields.videoUrl);

  if (!embedUrl) {
    return null;
  }

  return (
    <div className={clsx('w-full', getAlignmentClass(fields.alignment))}>
      {fields.title &&
        <h3 className="text-lg font-semibold mb-4"
          data-test="external-video-title"
        >
          {fields.title}
        </h3>
      }
      <div className={clsx('w-full', getAspectRatioClass(fields.aspectRatio))}>
        <iframe allowFullScreen
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          className="w-full h-full rounded-lg"
          sandbox="allow-scripts allow-same-origin allow-presentation"
          src={embedUrl}
          title={fields.title || 'External Video'}
        />
      </div>
    </div>
  );
};
